import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { ModalTypes } from "@enums/modal-types";
import { CellDropdownItem } from "@idw-types/common";
import { WorkflowDetailsValidationErrors } from "@idw-types/common/validation/workflow-details-validation-errors";
import { WorkflowChangeRequest } from "@idw-types/data/workflow-change-request";
import { FormattedWorkflowDetailsConfiguration } from "@idw-types/data/workflow-details-configuration";
import { createWorkflowChangeRequestRequest } from "@requests/service-configuration";
import { setAdvancedWorkflowDetails } from "@redux/actions/workflow-details-actions";
import { setPartialCommonReferenceData } from "@redux/actions/common-reference-data-actions";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import {
    clickWorkflowChangeRequestActionHandler,
    deployWorkflow,
    performWorkflowChangeRequestStep
} from "@utils/change-request-modal-util/workflow-change-request-modal-util";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import styles from "./workflow-details-dropdown-menu.less";
import { AxnAdminUserPermissions } from "@idw-types/data/axn-admin-user";
import {
    createWorkflowVerificationServiceRequest,
    CreateWorkflowVerificationServiceResponse
} from "@requests/service-configuration/create-workflow-verification-service";
import { AddVerificationServiceModal } from "@containers/verification-services/add-verification-service-modal/add-verification-service-modal";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { RpServiceTemplate } from "@idw-types/data/reference-data/generic";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { useChangeRequest } from "@hooks/use-change-requests";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type WorkflowDetailsDropdownMenuProps = {
    stateIsUnchanged: boolean;
    validationErrors: WorkflowDetailsValidationErrors;
    initialWorkflowDetailsConfiguration: FormattedWorkflowDetailsConfiguration;
    workflowDetailsConfiguration: FormattedWorkflowDetailsConfiguration;
    workflowChangeRequests: Array<WorkflowChangeRequest>;
    userPermissions: AxnAdminUserPermissions;
    rpServiceTemplates: Array<RpServiceTemplate>;
    axnRPServices: Array<AxnRpService>;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    initialRpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    rpServiceConfigurations: Array<FormattedRpServiceConfiguration>;
    setWorkflowDetailsConfigurationState: (workflowDetailsConfiguration: FormattedWorkflowDetailsConfiguration) => void;
    setWorkflowChangeRequest: (changeRequest: WorkflowChangeRequest) => void;
    setRpServiceConfigurationsState: (rpServiceConfigurations: Array<FormattedRpServiceConfiguration>) => void;
    getFormattedRpServiceForNewService: (data: CreateWorkflowVerificationServiceResponse) => FormattedRpServiceConfiguration;
}

export const WorkflowDetailsDropdownMenu = (props: WorkflowDetailsDropdownMenuProps) => {
    const {
        stateIsUnchanged,
        validationErrors,
        initialWorkflowDetailsConfiguration,
        workflowDetailsConfiguration,
        workflowChangeRequests,
        userPermissions,
        rpServiceTemplates,
        axnRPServices,
        simpleRPServiceWorkflows,
        initialRpServiceConfigurations,
        rpServiceConfigurations,
        setWorkflowChangeRequest,
        setWorkflowDetailsConfigurationState,
        setRpServiceConfigurationsState,
        getFormattedRpServiceForNewService
    } = props;

    const dispatch = useDispatch();
    const modalContext = useModalContext();

    const {
        changeRequestStatus,
        changeRequestType
    } = useChangeRequest({ changeRequests: workflowChangeRequests });

    const createWorkflowDetailChangeRequest = async () => {
        const { data, error } = await createWorkflowChangeRequestRequest(workflowDetailsConfiguration.id);
        if (data?.changeRequest) {
            displaySuccessModal(
                dispatch,
                resources.requests.createWorkflowChangeRequest.success.title,
                resources.requests.createWorkflowChangeRequest.success.message
            );

            setWorkflowChangeRequest(data.changeRequest);
            return data.changeRequest;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createWorkflowChangeRequest.error.title,
                resources.requests.createWorkflowChangeRequest.error.message,
                error
            );
        }
        return null;
    };

    const onSubmitCreateWorkflowChangeRequest = async (onModalClose: () => void) => {
        const response = await createWorkflowDetailChangeRequest();

        if (response) {
            onModalClose();
        }
        return response;
    }

    const onClickCreateAModifyChangeRequest = async () => {
        return clickWorkflowChangeRequestActionHandler(
            onSubmitCreateWorkflowChangeRequest,
            modalContext,
            workflowDetailsConfiguration.name,
            workflowDetailsConfiguration.id,
            changeRequestStatus,
            ModalTypes.CreateWorkflowChangeRequest
        );
    };

    const onSubmitCancelWorkflowChangeRequest = async (onModalClose: () => void) => {
        const response = await performWorkflowChangeRequestStep(
            dispatch,
            workflowDetailsConfiguration.id,
            ChangeRequestStatusTypes.CANCELED,
            null
        );

        if (response) {
            displaySuccessModal(
                dispatch,
                resources.requests.updateWorkflowChangeRequest.success.title,
                resources.requests.updateWorkflowChangeRequest.success.message
            );
            setWorkflowDetailsConfigurationState(workflowDetailsConfiguration);
            setWorkflowChangeRequest(response);
            onModalClose();
        }

        return response;
    }


    const onClickCancelChangeRequest = async () => {
        clickWorkflowChangeRequestActionHandler(
            onSubmitCancelWorkflowChangeRequest,
            modalContext,
            workflowDetailsConfiguration.name,
            workflowDetailsConfiguration.id,
            changeRequestStatus,
            ModalTypes.CancelWorkflowChangeRequest
        )
    };

    const onSubmitDeployNowWorkflowChangeRequest = async (onModalClose: () => void) => {
        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(changeRequestStatus);
        const response = await deployWorkflow(dispatch, workflowDetailsConfiguration.id, remainingChangeRequestStatuses, 0);

        if (response) {
            displaySuccessModal(
                dispatch,
                resources.requests.deployWorkflowChangeRequest.success.title,
                resources.requests.deployWorkflowChangeRequest.success.message
            );
            setWorkflowDetailsConfigurationState(workflowDetailsConfiguration);
            setWorkflowChangeRequest(response);
            onModalClose();
        }

        return response;
    }

    const onClickDeployChangesNow = async () => {
        clickWorkflowChangeRequestActionHandler(
            onSubmitDeployNowWorkflowChangeRequest,
            modalContext,
            workflowDetailsConfiguration.name,
            workflowDetailsConfiguration.id,
            changeRequestStatus,
            ModalTypes.DeployNowWorkflowChangeRequest
        );
    };

    const handleCreateService = () => {
        modalContext.setModalState({
            modalType: ModalTypes.AddVerificationService,
            renderFunction: renderAddVerificationServiceModal
        });
    };


    const handleCreateWorkflowService = async (rpServiceTemplateId: string, sourceRPServiceID: string, body: Record<string, any>) => {
        return await createWorkflowVerificationServiceRequest(rpServiceTemplateId, sourceRPServiceID, body, workflowDetailsConfiguration.id);
    }

    const handleAddRpServiceToWorkflow = async (data: CreateWorkflowVerificationServiceResponse) => {
        // We put the current applied profile into the new service data because Backend can't send unsaved updated workflow applied profile
        const formattedRpService = getFormattedRpServiceForNewService(data);
        const formattedRpServiceWithCurrentAppliedProfile: FormattedRpServiceConfiguration = {
            ...formattedRpService,
            appliedProfiles: workflowDetailsConfiguration.appliedProfiles,
            relyingParty: workflowDetailsConfiguration.relyingParty
        };

        if (formattedRpServiceWithCurrentAppliedProfile) {
            const { apiKey, id, serviceName, status } = formattedRpServiceWithCurrentAppliedProfile;
            dispatch(setPartialCommonReferenceData({
                axnRPServices: [
                    ...axnRPServices,
                    {
                        apiKey,
                        id,
                        relyingPartyID: workflowDetailsConfiguration.relyingParty?.id,
                        serviceName,
                        status
                    }
                ],
                    simpleAXNAdminRPServiceWorkflows: [
                        ...simpleRPServiceWorkflows,
                       { ...data.axnAdminRPServiceWorkflow, workflowID: workflowDetailsConfiguration.id }
                    ]
            }, CommonReferenceDataPageTypes.WORKFLOW_DETAILS));

            //update redux state to reflect new service having already been saved
            dispatch(setAdvancedWorkflowDetails({
                workflow: {
                    ...initialWorkflowDetailsConfiguration,
                    rpServiceWorkflows: [
                        ...initialWorkflowDetailsConfiguration.rpServiceWorkflows,
                        data.axnAdminRPServiceWorkflow
                    ]
                },
                rpServices: [
                    ...initialRpServiceConfigurations,
                    formattedRpService
                ]
            }, false));

            //update local state to include new service, but don't overwrite existing changes
            setWorkflowDetailsConfigurationState({
                ...workflowDetailsConfiguration,
                rpServiceWorkflows: [
                    ...workflowDetailsConfiguration.rpServiceWorkflows,
                    data.axnAdminRPServiceWorkflow
                ]
            });
            setRpServiceConfigurationsState([
                ...rpServiceConfigurations,
                formattedRpServiceWithCurrentAppliedProfile // we need to detect these changes for saving
            ]);
        }
    }

    const renderAddVerificationServiceModal = useMemo(() => (onModalClose: () => void) => {
        const activeAxnRpServices = axnRPServices.filter(service => service.status === EntityStatusTypes.ACTIVE);

        return (
            <AddVerificationServiceModal
                title={resources.pages.workflowDetails.modals.addServiceToWorkflow.title}
                description={resources.pages.workflowDetails.modals.addServiceToWorkflow.description}
                onModalClose={onModalClose}
                redirectUrl={null}
                userPermissions={userPermissions}
                verificationServices={activeAxnRpServices}
                rpServiceTemplates={rpServiceTemplates}
                updateStateForNewService={handleAddRpServiceToWorkflow}
                createServiceRequest={handleCreateWorkflowService}
            />
        );
    }, [
        rpServiceTemplates,
        axnRPServices,
        userPermissions,
        workflowDetailsConfiguration,
        rpServiceConfigurations,
        initialRpServiceConfigurations,
        simpleRPServiceWorkflows,
        initialWorkflowDetailsConfiguration,
        handleAddRpServiceToWorkflow,
        handleCreateWorkflowService
    ]);

    const hasUnsubmittedChangeRequest = useMemo(() => {
        if (workflowChangeRequests.length > 0) {
            return workflowChangeRequests[0].changeRequestStatus === ChangeRequestStatusTypes.UNSUBMITTED;
        }
        return false;
    }, [workflowChangeRequests]);

    const workflowDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];

        const {
            permissions
        } = workflowDetailsConfiguration;

        const prefix = 'workflow-details-dropdown-item';

        if (permissions.canModify && hasUnsubmittedChangeRequest && userPermissions.canCreateRPServices) {
            items.push({
                idSuffix: `${prefix}-add-service`,
                title: resources.pages.workflowDetails.buttons.addService.label,
                text: resources.pages.workflowDetails.buttons.addService.label,
                onClick: handleCreateService
            });
        }


        if (
            permissions.canSubmit &&
            permissions.canReview &&
            permissions.canDeploy &&
            changeRequestStatus &&
            [
                ChangeRequestStatusTypes.UNSUBMITTED,
                ChangeRequestStatusTypes.SUBMITTED,
                ChangeRequestStatusTypes.UNDER_REVIEW,
                ChangeRequestStatusTypes.APPROVED
            ].includes(changeRequestStatus)
        ) {
            let disabledText = resources.common.dropdownItems.deployChangesNow.unsavedChangesTooltip;
            if (validationErrors.count > 0) {
                disabledText = resources.common.dropdownItems.deployChangesNow.validationErrorsTooltip;
            }

            items.push({
                idSuffix: `${prefix}-deploy-changes-now`,
                title: resources.common.dropdownItems.deployChangesNow.label,
                text: resources.common.dropdownItems.deployChangesNow.label,
                disabled: !stateIsUnchanged || validationErrors.count > 0,
                disabledText: disabledText,
                onClick: onClickDeployChangesNow
            });
        }

        if (permissions.canModify && !changeRequestType && !changeRequestStatus) {
            items.push({
                idSuffix: `${prefix}-create-modify-change-request`,
                title: resources.common.dropdownItems.createModifyChangeRequest.label,
                text: resources.common.dropdownItems.createModifyChangeRequest.label,
                onClick: onClickCreateAModifyChangeRequest
            });
        }

        if (permissions.canCancel && changeRequestType && ![ChangeRequestStatusTypes.CANCELED, ChangeRequestStatusTypes.DEPLOYED].includes(changeRequestStatus)
        ) {
            items.push({
                idSuffix: `${prefix}-cancel-change-request`,
                title: resources.common.dropdownItems.cancelChangeRequest.label,
                text: resources.common.dropdownItems.cancelChangeRequest.label,
                onClick: onClickCancelChangeRequest
            });
        }

        // if (permissions.canArchive) {
        //     items.push({
        //         idSuffix: `${prefix}-archive-workflow`,
        //         title: resources.tables.workflows.dropdownItems.archiveWorkflow.label,
        //         text: resources.tables.workflows.dropdownItems.archiveWorkflow.label,
        //         className: styles.archiveButton,
        //         onClick: onClickArchiveWorkflow
        //     });
        // }

        return items;
    }, [
        workflowDetailsConfiguration,
        changeRequestStatus,
        changeRequestType,
        stateIsUnchanged,
        axnRPServices,
        initialRpServiceConfigurations,
        rpServiceConfigurations,
        simpleRPServiceWorkflows,
        initialWorkflowDetailsConfiguration
    ]);

    return (
        <TableCellWithDropdown
            className={styles.workflowDetailsDropdownMenu}
            value={""}
            dropdownText={"Actions"}
            items={workflowDropdownItems}
            uniqueId={workflowDetailsConfiguration.id}
        />
    );

}